/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { LazyPromise } from "@fluidframework/core-utils/internal";
import { IFluidDependencySynthesizer } from "./IFluidDependencySynthesizer.js";
/**
 * DependencyContainer is similar to a IoC Container. It takes providers and will
 * synthesize an object based on them when requested.
 * @legacy
 * @alpha
 */
export class DependencyContainer {
    get IFluidDependencySynthesizer() {
        return this;
    }
    constructor(...parents) {
        this.providers = new Map();
        this.parents = parents.filter((v) => v !== undefined);
    }
    /**
     * Add a new provider
     * @param type - Name of the Type T being provided
     * @param provider - A provider that will resolve the T correctly when asked
     * @throws - If passing a type that's already registered
     */
    register(type, provider) {
        if (this.providers.has(type)) {
            throw new Error(`Attempting to register a provider of type ${String(type)} that already exists`);
        }
        this.providers.set(type, provider);
    }
    /**
     * Remove a provider
     * @param type - Name of the provider to remove
     */
    unregister(type) {
        if (this.providers.has(type)) {
            this.providers.delete(type);
        }
    }
    /**
     * {@inheritDoc (IFluidDependencySynthesizer:interface).synthesize}
     */
    synthesize(optionalTypes, requiredTypes) {
        const base = {};
        this.generateRequired(base, requiredTypes);
        this.generateOptional(base, optionalTypes);
        Object.defineProperty(base, IFluidDependencySynthesizer, { get: () => this });
        return base;
    }
    /**
     * {@inheritDoc (IFluidDependencySynthesizer:interface).has}
     * @param excludeParents - If true, exclude checking parent registries
     */
    has(type, excludeParents) {
        if (this.providers.has(type)) {
            return true;
        }
        if (excludeParents !== true) {
            return this.parents.some((p) => p.has(type));
        }
        return false;
    }
    /**
     * @deprecated Needed for backwards compatability.
     */
    getProvider(provider) {
        // this was removed, but some partners have trouble with back compat where they
        // use invalid patterns with FluidObject and IFluidDependencySynthesizer
        // this is just for back compat until those are removed
        if (this.has(provider)) {
            if (this.providers.has(provider)) {
                return this.providers.get(provider);
            }
            for (const parent of this.parents) {
                if (parent instanceof DependencyContainer) {
                    return parent.getProvider(provider);
                }
                else {
                    // older implementations of the IFluidDependencySynthesizer exposed getProvider
                    const maybeGetProvider = parent;
                    if (maybeGetProvider?.getProvider !== undefined) {
                        return maybeGetProvider.getProvider(provider);
                    }
                }
            }
        }
    }
    generateRequired(base, types) {
        if (types === undefined) {
            return;
        }
        for (const key of Object.keys(types)) {
            const provider = this.resolveProvider(key);
            if (provider === undefined) {
                throw new Error(`Object attempted to be created without registered required provider ${String(key)}`);
            }
            Object.defineProperty(base, key, provider);
        }
    }
    generateOptional(base, types) {
        if (types === undefined) {
            return;
        }
        for (const key of Object.keys(types)) {
            // back-compat: in 0.56 we allow undefined in the types, but we didn't before
            // this will keep runtime back compat, eventually we should support undefined properties
            // rather than properties that return promises that resolve to undefined
            const provider = this.resolveProvider(key) ?? { get: async () => undefined };
            Object.defineProperty(base, key, provider);
        }
    }
    resolveProvider(t) {
        // If we have the provider return it
        const provider = this.providers.get(t);
        if (provider === undefined) {
            for (const parent of this.parents) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const sp = { [t]: t };
                const syn = parent.synthesize(sp, {});
                const descriptor = Object.getOwnPropertyDescriptor(syn, t);
                if (descriptor !== undefined) {
                    return descriptor;
                }
            }
            return undefined;
        }
        // The double nested gets are required for lazy loading the provider resolution
        if (typeof provider === "function") {
            return {
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                get() {
                    if (provider && typeof provider === "function") {
                        return Promise.resolve(this[IFluidDependencySynthesizer])
                            .then(async (fds) => provider(fds))
                            .then((p) => p?.[t]);
                    }
                },
            };
        }
        return {
            get() {
                if (provider) {
                    return new LazyPromise(async () => {
                        return Promise.resolve(provider).then((p) => {
                            if (p) {
                                return p[t];
                            }
                        });
                    });
                }
            },
        };
    }
}
//# sourceMappingURL=dependencyContainer.js.map